/**
 * Calls whose responses need reshaping before the app can use them. Everything else calls `api`
 * directly (errors arrive as ApiError either way).
 */
import type {
  LegacyDeliverSearchPinRequestDto,
  LegacyDeliverSearchPinResponseDto,
  ScreeningDto,
  SHLinkAccessLogDto,
  SHLinkDescriptionDto,
  UserResponseDto,
} from "@docket/consumer-app-client-typescript-axios";
import { DateTime } from "luxon";
import { LEGACY_API_DOMAIN, PROTOCOL } from "../globals";
import { assertNever } from "../utils/assert-never";
import { api } from "./client";
import {
  LegalSex,
  legalSexFromString,
  ReportType,
  type LegacyGetRecordDataDtoWithIntId,
  type LegacyGetRecordsResponseDtoWithIntId,
  type LegacyGetSearchDataDtoWithIntId,
  type LegacyGetSearchesResponseDtoWithIntId,
} from "./types";

/** The API sends numeric ids as strings; the app uses numbers. */
const withIntId = <T extends { id: string }>(item: T): Omit<T, "id"> & { id: number } => ({
  ...item,
  id: parseInt(item.id),
});

// ==== Sign-in

export async function loginWithFirebase(token: string): Promise<UserResponseDto> {
  const { data } = await api.auth.firebase({ token });
  if (data.legal_sex) {
    // Super fun: legal sex is returned as a full string, e.g. "male", instead of "M" for this API.
    data.legal_sex = legalSexFromString(data.legal_sex);
  }
  return data;
}

export async function loginWithAuthCode(
  code: string,
  stateIdentifier: string,
): Promise<UserResponseDto> {
  const { data } = await api.auth.oidc(stateIdentifier, { code });
  // Super fun: legal sex is returned as a full string, e.g. "male", instead of "M" for this API.
  data.legal_sex = legalSexFromString(data.legal_sex || LegalSex.Unknown);
  return data;
}

/** OIDC sign-in happens on the legacy API's domain, which redirects back to the app. */
export function initiateOidcAuth(stateIdentifier: string): void {
  window.location.href = `${PROTOCOL}://${LEGACY_API_DOMAIN}/auth/${stateIdentifier}`;
}

// ==== Immunization searches

export async function getIzSearches(
  iconTapped: boolean,
): Promise<LegacyGetSearchesResponseDtoWithIntId> {
  const { data } = await api.railsLegacyV2.getSearches(iconTapped.toString());
  return { data: (data.data ?? []).map(withIntId) };
}

/** A search, or null while it is still being dequeued (the API answers 204 with no body). */
export async function getIzSearch(
  searchUid: string,
  jurisdiction: string,
): Promise<LegacyGetSearchDataDtoWithIntId | null> {
  const response = await api.railsLegacyV2.getSearch(jurisdiction, searchUid);
  if (response.status === 204) {
    return null;
  }
  // getSearch returns one search under `data`, not an array.
  return withIntId(response.data.data);
}

/** Exactly one of `sms`, `email` or `voice` says where to send the PIN. */
export async function sendImmunizationSearchPIN(
  searchUid: string,
  params: LegacyDeliverSearchPinRequestDto,
  jurisdiction: string,
): Promise<LegacyDeliverSearchPinResponseDto> {
  const channels = [params.sms, params.email, params.voice].filter(Boolean).length;
  if (channels !== 1) {
    throw new Error("You can only request one of SMS, Email, or Voice for PIN verification");
  }
  return (await api.railsLegacyV2.deliverSearchPin(jurisdiction, searchUid, params)).data;
}

// ==== Records

export async function getIzRecord(
  searchUid: string,
  jurisdiction: string,
): Promise<LegacyGetRecordDataDtoWithIntId> {
  const { data } = await api.railsLegacyV2.getRecord(jurisdiction, searchUid);
  return withIntId(data.data);
}

export async function getAllIzRecords(): Promise<LegacyGetRecordsResponseDtoWithIntId> {
  const { data } = await api.railsLegacyV2.getRecords();
  return { data: (data.data ?? []).map(withIntId) };
}

export async function getIzScreeningRecord(
  searchUid: string,
  jurisdiction: string,
): Promise<ScreeningDto[]> {
  return (await api.railsLegacyV2.getScreenings(jurisdiction, searchUid)).data.screenings;
}

/** Ask the API to refresh every record. Resolves to the estimated wait, in seconds. */
export async function refreshRecords(): Promise<number> {
  return (await api.railsLegacyV2.refreshRecordsAsync()).data.estimated_wait_time;
}

/** The report PDF. (The generated client types these responses as File; they're bytes.) */
export async function getRecordReport(
  searchUid: string,
  reportType: ReportType,
  jurisdiction: string,
): Promise<ArrayBuffer> {
  const rails = api.railsLegacyV2;
  const options = { responseType: "arraybuffer" } as const;
  let response: { data: unknown };
  switch (reportType) {
    case ReportType.HISTORY_REPORT:
      response = await rails.getHistoryReport(
        jurisdiction,
        searchUid,
        undefined,
        undefined,
        options,
      );
      break;
    case ReportType.SCHOOL_REPORT:
      response = await rails.getSchoolReport(
        jurisdiction,
        searchUid,
        undefined,
        undefined,
        options,
      );
      break;
    case ReportType.FORECAST_REPORT:
      response = await rails.getForecastReport(
        jurisdiction,
        searchUid,
        undefined,
        undefined,
        options,
      );
      break;
    case ReportType.PERSONAL_REPORT:
      response = await rails.getPersonalImmunizationReport(
        jurisdiction,
        searchUid,
        undefined,
        undefined,
        options,
      );
      break;
    default:
      assertNever(reportType);
  }
  return response.data as ArrayBuffer;
}

/** The SMART Health Card QR payload ("shc:/…"), as text. */
export async function getVCICode(recordUid: string, jurisdiction: string): Promise<string> {
  const response = await api.railsLegacyV2.getVaccineCard(
    jurisdiction,
    recordUid,
    "COVID.smart-health-card",
    undefined,
    undefined,
    // `data: null` keeps axios from dropping the Content-Type header: https://github.com/axios/axios/issues/86
    { headers: { "Content-Type": "qr/numeric" }, data: null, responseType: "text" },
  );
  return response.data as unknown as string;
}

// ==== SMART Health Links

const PAGE_SIZE = 50;

/** The active links. Deleted links are only expired on the server, so they come back too. */
export async function getSmartHealthLinks(): Promise<SHLinkDescriptionDto[]> {
  const items: SHLinkDescriptionDto[] = [];
  let offset: number | undefined = 0;
  while (offset !== undefined) {
    const { data } = await api.smartHealthLinks.getShlinks(PAGE_SIZE, offset);
    items.push(...(data.items ?? []));
    offset = data.nextOffset;
  }
  return items.filter((item) => item.status === "active");
}

export async function getSmartHealthLinkAccessLogs(id: string): Promise<SHLinkAccessLogDto[]> {
  const items: SHLinkAccessLogDto[] = [];
  let offset: number | undefined = 0;
  while (offset !== undefined) {
    const { data } = await api.smartHealthLinks.getShlinkAccessLogs(id, PAGE_SIZE, offset);
    items.push(...(data.items ?? []));
    offset = data.nextOffset;
  }
  return items;
}

/** Links are only soft deleted on the server (for compliance): expire it as of yesterday. */
export async function deleteSmartHealthLink(id: string): Promise<void> {
  await api.smartHealthLinks.updateShlink(id, {
    expirationDate: DateTime.now().minus({ days: 1 }).toISO(),
  });
}
